import { SliceComponentProps } from "@prismicio/react";
import _get from "lodash/get";
import { RegisterOptions, useFormContext } from "react-hook-form";

import ErrorBoundary from "components/errorBoundary/ErrorBoundary";
import MultiText from "components/form/multiText/MultiText";
import { MultiTextFieldSlice } from "prismicio-types";
import { useGetProduct } from "queries/legacy/product-queries";
import { FieldSliceContext, MultiTextSchemaField } from "types/schema";
import {
  prismicFieldConfigurationError,
  SSR_CONFIGURATION_ERROR,
} from "utils/error-utils";
import { CANVAS_FALLBACK_CONTENT } from "utils/fallback-content";
import { useInterpolate } from "utils/prismic-utils";
import {
  getInputPropsFromSchemaRules,
  isFieldType,
  useFindByKey,
} from "utils/schema-utils";

import styles from "../Fields.module.scss";

export default function MultiTextField({
  slice,
}: SliceComponentProps<MultiTextFieldSlice, FieldSliceContext>) {
  const {
    formState: { errors },
  } = useFormContext();

  const { data: product } = useGetProduct();

  const findByKey = useFindByKey();
  const interpolate = useInterpolate();

  if (!product) {
    throw SSR_CONFIGURATION_ERROR;
  }

  const schemaKey = slice.primary.muse_schema_key;

  if (!schemaKey) {
    throw new Error("Missing Muse Schema Key");
  }

  const field = findByKey(schemaKey);

  if (!field) {
    return null;
  }

  if (!isFieldType<MultiTextSchemaField>(field, "multi_text")) {
    throw prismicFieldConfigurationError(
      schemaKey,
      "multi_text",
      field.metas[0].canvasComponent
    );
  }

  // Rules is being used for minimum and maximum fields
  const { maxLength: maxFields, minLength: minFields } =
    getInputPropsFromSchemaRules(field.rules);

  if ((!minFields && minFields !== 0) || !maxFields) {
    throw new Error("Min and max fields are required");
  }

  // Get the actual input rules from `items`
  const { maxLength, minLength } = getInputPropsFromSchemaRules(
    field.items[0].rules
  );

  const isEmptyField =
    field.allow?.[0] === "" || field.allow?.length === 0 || field.allow === "";

  const registerOptions: RegisterOptions = {
    ...(maxLength && {
      maxLength: {
        value: maxLength,
        message: interpolate(
          product.content.canvas.form_field_max_length_error_message ||
            CANVAS_FALLBACK_CONTENT.form_field_max_length_error_message
        ),
      },
    }),
    ...(minLength && {
      minLength: {
        value: minLength,
        message: interpolate(
          product.content.canvas.form_field_min_length_error_message ||
            CANVAS_FALLBACK_CONTENT.form_field_min_length_error_message
        ),
      },
    }),
    required:
      field.flags?.presence === "required" &&
      !isEmptyField &&
      interpolate(
        product.content.canvas.form_field_required_error_message ||
          CANVAS_FALLBACK_CONTENT.form_field_required_error_message
      ),
  };

  const error = errors && _get(errors, field.urlKey)?.message;

  return (
    <div className={styles.field}>
      {slice.primary.label && (
        <label className={styles.label} htmlFor={field.urlKey}>
          {interpolate(slice.primary.label)}
        </label>
      )}

      <ErrorBoundary>
        <MultiText
          id={field.urlKey}
          maxFields={maxFields}
          minFields={minFields}
          name={field.urlKey}
          placeholder={interpolate(slice.primary.input_placeholder || "")}
          registerOptions={registerOptions}
        />
      </ErrorBoundary>

      {error && <p className={styles.error}>{error as string}</p>}
    </div>
  );
}
